import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, User, ChevronLeft, ChevronRight, Grid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_isalon-modern/artifacts/cd5s20vv_isalon2022-01.png";

const StylistCalendarPage = () => {
  const { stylistName } = useParams();
  const { authFetch } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentDate, setCurrentDate] = useState(new Date());

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/calendar/stylist/${encodeURIComponent(stylistName)}`);
      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      } else {
        toast.error('Failed to load calendar');
      }
    } catch (err) {
      if (err.message) toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, stylistName]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Parse date string to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Try various date formats
    // Format: "Monday 24th March" or "2026-03-24"
    if (dateStr.includes('-')) {
      return new Date(dateStr);
    }
    
    // Parse "Day DDth Month" format
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    const parts = dateStr.split(' ');
    if (parts.length >= 3) {
      const dayNum = parseInt(parts[1].replace(/\D/g, ''));
      const monthName = parts[2];
      const monthIndex = months.indexOf(monthName);
      if (monthIndex !== -1 && dayNum) {
        const year = new Date().getFullYear();
        return new Date(year, monthIndex, dayNum);
      }
    }
    
    return null;
  };

  // Get week dates
  const getWeekDates = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + 1); // Monday
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  // Get month dates
  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const dates = [];
    // Add padding for days before first of month
    const startPadding = (firstDay.getDay() + 6) % 7; // Monday = 0
    for (let i = startPadding; i > 0; i--) {
      const d = new Date(year, month, 1 - i);
      dates.push({ date: d, isCurrentMonth: false });
    }
    
    // Add month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add padding after
    const endPadding = (7 - (dates.length % 7)) % 7;
    for (let i = 1; i <= endPadding; i++) {
      dates.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return dates;
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => {
      const aptDate = parseDate(apt.date);
      if (!aptDate) return false;
      return aptDate.toDateString() === date.toDateString();
    });
  };

  // Navigate
  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  const weekDates = getWeekDates(currentDate);
  const monthDates = getMonthDates(currentDate);
  const today = new Date();

  return (
    <div className="min-h-screen bg-black py-8 px-4" data-testid="stylist-calendar">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <img src={LOGO_URL} alt="iSalon UK" className="h-10" />
            </Link>
            <div>
              <h1 className="font-heading text-2xl text-white">{decodeURIComponent(stylistName)}'s Calendar</h1>
              <p className="text-white/50 text-sm">Appointment Schedule</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Link to="/backoffice">
              <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10">
                <Grid size={16} className="mr-2" />
                All Stylists
              </Button>
            </Link>
          </div>
        </div>

        {/* View Toggle & Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              size="icon"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ChevronLeft size={20} />
            </Button>
            <span className="text-white font-sans min-w-[200px] text-center">
              {viewMode === 'week' 
                ? `${formatDate(weekDates[0])} - ${formatDate(weekDates[6])}`
                : formatMonthYear(currentDate)
              }
            </span>
            <Button
              onClick={() => navigate(1)}
              variant="outline"
              size="icon"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ChevronRight size={20} />
            </Button>
            <Button
              onClick={() => setCurrentDate(new Date())}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10 ml-2"
            >
              Today
            </Button>
          </div>
          
          <div className="flex bg-zinc-900 rounded-lg p-1">
            <Button
              onClick={() => setViewMode('week')}
              size="sm"
              className={`rounded-md ${viewMode === 'week' 
                ? 'bg-isalonuk-accent text-black' 
                : 'bg-transparent text-white hover:bg-white/10'}`}
              data-testid="view-week"
            >
              Week
            </Button>
            <Button
              onClick={() => setViewMode('month')}
              size="sm"
              className={`rounded-md ${viewMode === 'month' 
                ? 'bg-isalonuk-accent text-black' 
                : 'bg-transparent text-white hover:bg-white/10'}`}
              data-testid="view-month"
            >
              Month
            </Button>
          </div>
        </div>

        {/* Week View */}
        {viewMode === 'week' && (
          <div className="grid grid-cols-7 gap-2" data-testid="week-view">
            {weekDates.map((date, idx) => {
              const dayAppointments = getAppointmentsForDate(date);
              const isToday = date.toDateString() === today.toDateString();
              
              return (
                <div key={idx} className="min-h-[300px]">
                  <div className={`text-center py-2 rounded-t-lg ${isToday ? 'bg-isalonuk-accent text-black' : 'bg-zinc-800 text-white'}`}>
                    <div className="text-xs font-sans">{date.toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                    <div className="text-lg font-bold">{date.getDate()}</div>
                  </div>
                  <div className="bg-zinc-900 rounded-b-lg p-2 min-h-[260px] border border-white/10">
                    {dayAppointments.length === 0 ? (
                      <p className="text-white/30 text-xs text-center mt-4">No appointments</p>
                    ) : (
                      <div className="space-y-2">
                        {dayAppointments.map((apt, i) => (
                          <div 
                            key={i} 
                            className={`p-2 rounded-lg text-xs ${
                              apt.status === 'pending' 
                                ? 'bg-yellow-500/20 border border-yellow-500/30' 
                                : 'bg-isalonuk-accent/20 border border-isalonuk-accent/30'
                            }`}
                          >
                            <div className="flex items-center gap-1 text-white/80 mb-1">
                              <Clock size={10} />
                              <span>{apt.time}</span>
                            </div>
                            <div className="text-white font-medium">{apt.customer_first_name}</div>
                            {apt.status === 'pending' && (
                              <span className="text-yellow-400 text-[10px]">Pending</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Month View */}
        {viewMode === 'month' && (
          <div data-testid="month-view">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <div key={day} className="text-center text-white/50 text-xs py-2 font-sans">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {monthDates.map((item, idx) => {
                const dayAppointments = getAppointmentsForDate(item.date);
                const isToday = item.date.toDateString() === today.toDateString();
                
                return (
                  <div 
                    key={idx} 
                    className={`min-h-[100px] p-1 rounded-lg border ${
                      item.isCurrentMonth 
                        ? 'bg-zinc-900 border-white/10' 
                        : 'bg-zinc-950 border-transparent'
                    }`}
                  >
                    <div className={`text-xs mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                      isToday 
                        ? 'bg-isalonuk-accent text-black font-bold' 
                        : item.isCurrentMonth ? 'text-white' : 'text-white/30'
                    }`}>
                      {item.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayAppointments.slice(0, 3).map((apt, i) => (
                        <div 
                          key={i} 
                          className={`text-[10px] px-1 py-0.5 rounded truncate ${
                            apt.status === 'pending' 
                              ? 'bg-yellow-500/30 text-yellow-200' 
                              : 'bg-isalonuk-accent/30 text-isalonuk-accent'
                          }`}
                        >
                          {apt.time} {apt.customer_first_name}
                        </div>
                      ))}
                      {dayAppointments.length > 3 && (
                        <div className="text-[10px] text-white/50 px-1">
                          +{dayAppointments.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-6 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-isalonuk-accent/30 border border-isalonuk-accent/50"></div>
            <span className="text-white/60">Confirmed</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-yellow-500/30 border border-yellow-500/50"></div>
            <span className="text-white/60">Pending</span>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <Link to="/" className="text-isalonuk-accent font-sans text-sm hover:underline">
            ← Back to iSalon Website
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StylistCalendarPage;
